/**
 * WebSocket Context Provider
 * Single WebSocket connection shared across entire app
 */
'use client';

import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';

interface WebSocketMessage {
  type: string;
  video_id?: string;
  clip_id?: string;
  question_count?: number;
  data?: any;
}

interface WebSocketContextType {
  isConnected: boolean;
  subscribe: (callback: (message: WebSocketMessage) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttempts = useRef(0);
  const subscribersRef = useRef<Set<(message: WebSocketMessage) => void>>(new Set());
  const hasInitialized = useRef(false);

  const connect = () => {
    // Prevent multiple connection attempts
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('⚠️ WebSocket already connected or connecting');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('⚠️ No auth token, skipping WebSocket');
      return;
    }

    // Always use the current page origin for WS so local dev works even when
    // NEXT_PUBLIC_API_URL points at production (/api is same host as the app).
    const wsBase = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    const fullUrl = `${wsBase}/ws?token=${encodeURIComponent(token)}`;

    console.log('🔌 Connecting to WebSocket:', wsBase);

    try {
      const ws = new WebSocket(fullUrl);

      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          
          // Don't log every ping/pong
          if (message.type !== 'pong') {
            console.log('📨 WebSocket message:', message);
          }

          // Notify all subscribers
          subscribersRef.current.forEach(callback => {
            try {
              callback(message);
            } catch (err) {
              console.error('Error in WebSocket subscriber:', err);
            }
          });
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error');
      };

      ws.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        setIsConnected(false);
        wsRef.current = null;

        // Reconnect with exponential backoff (max 30 seconds)
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current++;
          connect();
        }, delay);
      };

      wsRef.current = ws;

      // Keepalive ping every 30 seconds
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      ws.addEventListener('close', () => {
        clearInterval(pingInterval);
      });

    } catch (err) {
      console.error('Error creating WebSocket:', err);
    }
  };

  useEffect(() => {
    // Only initialize once
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const subscribe = (callback: (message: WebSocketMessage) => void) => {
    subscribersRef.current.add(callback);
    
    // Return unsubscribe function
    return () => {
      subscribersRef.current.delete(callback);
    };
  };

  return (
    <WebSocketContext.Provider value={{ isConnected, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
}

// Simplified hook for common use cases
export function useWebSocketMessages(
  onMessage: (message: WebSocketMessage) => void
) {
  const { isConnected, subscribe } = useWebSocketContext();

  useEffect(() => {
    const unsubscribe = subscribe(onMessage);
    return unsubscribe;
  }, [onMessage, subscribe]);

  return { isConnected };
}











